const stableJson = (value) => {
    try {
        return JSON.stringify(value);
    }
    catch {
        return String(value);
    }
};
export const resolveMenuEntityId = (entity) => String(entity?._id || entity?.id || '');
export const recordsDiffer = (left, right) => stableJson(left) !== stableJson(right);
const MENU_ITEM_UI_KEYS = [
    'name',
    'description',
    'price',
    'priceCent',
    'originalPrice',
    'originalPriceCent',
    'available',
    'isAvailable',
    'hasSizes',
    'priceRange',
    'image',
    'imageUrl',
    'sortOrder',
    'sizes',
    'badges',
    'tags',
    'allergens',
    'deposit',
    'popular',
    'isVegetarian',
    'isVegan',
    'isGlutenFree',
    'preparationTime',
    // Produkt-Konfiguration (Modal: Größen, Pflichtfelder, Extras)
    'mandatoryIngredients',
    'extraIngredients',
    'removableIngredients',
    'ingredients',
    'customizable'
];
const normalizeMenuItemSnapshot = (item) => {
    const category = item.category;
    const categoryKey = typeof category === 'string'
        ? category
        : category && typeof category === 'object'
            ? String(category._id
                || category.id
                || category.slug
                || category.name
                || '')
            : String(item.categoryId || '');
    return {
        id: resolveMenuEntityId(item),
        category: categoryKey,
        available: item.available ?? item.isAvailable ?? true,
        ...Object.fromEntries(MENU_ITEM_UI_KEYS
            .filter((key) => key !== 'available' && key !== 'isAvailable')
            .map((key) => [key, item[key]]))
    };
};
export const menuItemsEquivalent = (left, right) => !recordsDiffer(normalizeMenuItemSnapshot(left), normalizeMenuItemSnapshot(right));
/** Aktualisiert nur UI-relevante Produktfelder, wenn sich der sichtbare Inhalt ändert. */
export const patchMenuItemIfChanged = (target, source) => {
    if (menuItemsEquivalent(target, source)) {
        return false;
    }
    const sourceRecord = source;
    const targetRecord = target;
    let changed = false;
    for (const key of MENU_ITEM_UI_KEYS) {
        if (!(key in sourceRecord))
            continue;
        if (recordsDiffer(targetRecord[key], sourceRecord[key])) {
            targetRecord[key] = sourceRecord[key];
            changed = true;
        }
    }
    const resolvedId = resolveMenuEntityId(source);
    if (resolvedId && target.id !== resolvedId) {
        target.id = resolvedId;
        changed = true;
    }
    if (source._id && target._id !== source._id) {
        target._id = source._id;
        changed = true;
    }
    return changed;
};
/** Aktualisiert nur Felder, deren Wert sich geändert hat. */
export const patchRecordIfChanged = (target, source) => {
    let changed = false;
    for (const key of Object.keys(source)) {
        if (recordsDiffer(target[key], source[key])) {
            target[key] = source[key];
            changed = true;
        }
    }
    return changed;
};
const reorderById = (existing, order, resolveId) => {
    const unchanged = order.length === existing.length
        && order.every((id, index) => resolveId(existing[index]) === id);
    if (unchanged)
        return;
    existing.sort((left, right) => {
        const leftIndex = order.indexOf(resolveId(left));
        const rightIndex = order.indexOf(resolveId(right));
        return (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex)
            - (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex);
    });
};
export const mergeMenuItemArraysInPlace = (existing, incoming) => {
    const byId = new Map(existing.map((item) => [resolveMenuEntityId(item), item]));
    const nextIds = new Set();
    let changed = false;
    for (const sourceItem of incoming) {
        const id = resolveMenuEntityId(sourceItem);
        if (!id)
            continue;
        nextIds.add(id);
        const current = byId.get(id);
        if (current) {
            if (patchMenuItemIfChanged(current, sourceItem))
                changed = true;
        }
        else {
            existing.push(sourceItem);
            changed = true;
        }
    }
    for (let index = existing.length - 1; index >= 0; index -= 1) {
        const id = resolveMenuEntityId(existing[index]);
        if (!nextIds.has(id)) {
            existing.splice(index, 1);
            changed = true;
        }
    }
    const order = incoming.map((item) => resolveMenuEntityId(item)).filter(Boolean);
    const before = existing.map((item) => resolveMenuEntityId(item)).join('|');
    reorderById(existing, order, (item) => resolveMenuEntityId(item));
    const after = existing.map((item) => resolveMenuEntityId(item)).join('|');
    return changed || before !== after;
};
export const mergeCategoryArraysInPlace = (existing, incoming) => {
    const byId = new Map(existing.map((category) => [category.id, category]));
    const nextIds = new Set();
    let changed = false;
    for (const sourceCategory of incoming) {
        if (!sourceCategory.id)
            continue;
        nextIds.add(sourceCategory.id);
        const current = byId.get(sourceCategory.id);
        if (current) {
            if (current.name !== sourceCategory.name) {
                current.name = sourceCategory.name;
                changed = true;
            }
            if (current.description !== sourceCategory.description) {
                current.description = sourceCategory.description;
                changed = true;
            }
            if (current.icon !== sourceCategory.icon) {
                current.icon = sourceCategory.icon;
                changed = true;
            }
            if (current.color !== sourceCategory.color) {
                current.color = sourceCategory.color;
                changed = true;
            }
            if (mergeMenuItemArraysInPlace(current.items, sourceCategory.items)) {
                changed = true;
            }
        }
        else {
            existing.push({
                ...sourceCategory,
                items: [...sourceCategory.items]
            });
            changed = true;
        }
    }
    for (let index = existing.length - 1; index >= 0; index -= 1) {
        if (!nextIds.has(existing[index].id)) {
            existing.splice(index, 1);
            changed = true;
        }
    }
    const order = incoming.map((category) => category.id);
    const before = existing.map((category) => category.id).join('|');
    reorderById(existing, order, (category) => category.id);
    const after = existing.map((category) => category.id).join('|');
    return changed || before !== after;
};
export const mergeObjectArraysInPlace = (existing, incoming, resolveId) => {
    const byId = new Map(existing.map((item) => [resolveId(item), item]));
    const nextIds = new Set();
    let changed = false;
    for (const sourceItem of incoming) {
        const id = resolveId(sourceItem);
        if (!id)
            continue;
        nextIds.add(id);
        const current = byId.get(id);
        if (current) {
            if (patchRecordIfChanged(current, sourceItem))
                changed = true;
        }
        else {
            existing.push(sourceItem);
            changed = true;
        }
    }
    for (let index = existing.length - 1; index >= 0; index -= 1) {
        if (!nextIds.has(resolveId(existing[index]))) {
            existing.splice(index, 1);
            changed = true;
        }
    }
    return changed;
};
export const menuBundlesEquivalent = (left, right) => {
    const leftProducts = Array.isArray(left.products) ? left.products : [];
    const rightProducts = Array.isArray(right.products) ? right.products : [];
    if (leftProducts.length !== rightProducts.length)
        return false;
    const rightById = new Map(rightProducts.map((product) => [resolveMenuEntityId(product), product]));
    for (const product of leftProducts) {
        const id = resolveMenuEntityId(product);
        const other = rightById.get(id);
        if (!other || !menuItemsEquivalent(product, other)) {
            return false;
        }
    }
    return true;
};
